import React, { forwardRef, useEffect, useState } from 'react';
import styles from './guidePlaces.module.css';
import CharCount from '../../../charCount/CharCount';
import ReactQuill from 'react-quill';
import { modules } from '../../../../utils/quillModules';
import { formats } from '../../../../utils/quillFormats';
import useSectionHighlightRef from '../../../../hooks/useSectionHighlightRef';
import { toast } from 'react-toastify';

function isDescriptionEmpty(description) {
    if (!description) return true;

    const text = description
        .replace(/<[^>]*>/g, '')
        .replace(/&nbsp;/g, ' ')
        .trim();

    return !text;
}

const PlacesSection = forwardRef(
    ({ title = 'Places', itemLabel = 'Place', minItems = 1, data, setData, isEdit, isSupport }, ref) => {
        const [count, setCount] = useState(data?.places?.length || 0);
        const { sectionRef, highlightField } = useSectionHighlightRef(ref);

        useEffect(() => {
            setCount(data?.places?.length || 0);
        }, [isEdit, data?.places]);

        const handlePlaceChange = (eventOrValue, index, type) => {
            setData((prev) => {
                const updatedArray = prev.places.map((item, itemKey) => {
                    if (index !== itemKey) return item;

                    if (type === 'name') {
                        return {
                            ...item,
                            name: eventOrValue.target.value
                        };
                    }

                    return {
                        ...item,
                        description: eventOrValue
                    };
                });

                return {
                    ...prev,
                    places: updatedArray
                };
            });
        };

        function addItem() {
            const places = data?.places || [];

            // Validate current place
            if (places.length > 0) {
                const lastPlace = places[places.length - 1];

                if (!lastPlace.name?.trim()) {
                    alert(`Please enter ${itemLabel} ${places.length} name.`);
                    return;
                }

                if (isDescriptionEmpty(lastPlace.description)) {
                    alert(`Please enter ${itemLabel} ${places.length} description.`);
                    return;
                }
            }

            setData((prevState) => ({
                ...prevState,
                places: [
                    ...(prevState.places || []),
                    {
                        name: '',
                        description: ''
                    }
                ],
                price: [
                    ...(prevState.price || []),
                    {
                        actual: 0,
                        tax: 0,
                        total: 0,
                        reserve: 0,
                        higherPrice: null,
                        discountPer: null,
                        name: '',
                        perPersonPrice: 0,
                        count: 1,
                        terms: [],
                        instalments: []
                    }
                ]
            }));
        }

        const deleteItem = () => {
            if ((data?.places?.length || 0) <= minItems) return;

            setData((prevState) => ({
                ...prevState,
                places: (prevState.places || []).slice(0, -1),
                price: (prevState.price || []).slice(0, -1)
            }));
        };

        return (
            <div ref={sectionRef}>
                <h2 className={styles.orgPackUpH2}>{title}</h2>

                <div className={styles.orgPackUpSecDiv}>
                    {data?.places?.map((item, index) => (
                        <div key={index}>
                            <p className={styles.activityLabel}>
                                {itemLabel} {index + 1}
                            </p>

                            {/* Name */}
                            <div className={styles.activityInDiv}>
                                <label className={styles.activityLabel}>Name</label>

                                <div className={styles.activityInputDiv}>
                                    <input
                                        type='text'
                                        name='name'
                                        placeholder={`${itemLabel} ${index + 1} Name`}
                                        className={`${styles.activityInput} ${
                                            highlightField === 'name' ? 'error-highlight' : ''
                                        }`}
                                        value={item?.name || ''}
                                        onChange={(event) => handlePlaceChange(event, index, 'name')}
                                        disabled={
                                            !isSupport &&
                                            isEdit &&
                                            data.status !== 'inactive' &&
                                            data.status !== 'rejected'
                                        }
                                    />

                                    <CharCount string={item?.name || ''} min={3} limit={60} />
                                </div>
                            </div>

                            {/* Description */}
                            <div className={styles.activityDiv}>
                                <label className={styles.activityLabel}>Description</label>

                                <div className={styles.activityDescDiv}>
                                    <ReactQuill
                                        theme='snow'
                                        placeholder={`${itemLabel} ${index + 1} Description`}
                                        value={item?.description || ''}
                                        onChange={(value) => handlePlaceChange(value, index, 'description')}
                                        modules={modules}
                                        formats={formats}
                                        readOnly={
                                            !isSupport &&
                                            isEdit &&
                                            data.status !== 'inactive' &&
                                            data.status !== 'rejected'
                                        }
                                    />

                                    <CharCount string={item?.description || ''} min={20} limit={150} />
                                </div>
                            </div>
                        </div>
                    ))}

                    {/* Buttons */}
                    <div className={`alignButtons ${styles.activityBtn}`}>
                        <button type='button' onClick={addItem} className={styles.activityAddBtn}>
                            Add {itemLabel}
                        </button>

                        <button
                            type='button'
                            onClick={deleteItem}
                            className='itineraryDelBtn'
                            disabled={(data?.places?.length || 0) <= minItems}
                        >
                            Delete {itemLabel}
                        </button>
                    </div>
                </div>
            </div>
        );
    }
);

export default PlacesSection;
